import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    type LegalDocumentAssignmentPublic,
    type OrganizationUserAgreementSource,
} from '@pillar-portal/api-types';

function formatTimestamp(timestamp: string): string {
    return formatTimestampForMerchantUi(timestamp, SYSTEM_DISPLAY_TIME_ZONE);
}

export function formatUserAgreementSource(source: OrganizationUserAgreementSource): string {
    switch (source.kind) {
        case 'own':
            return 'Assigned to this Organization';
        case 'inherited':
            return `Inherited from ${source.organization_name}`;
        case 'platform_default':
            return 'Platform default';
    }
}

export function formatAcceptanceProgress(
    acceptedUserCount: number,
    requiredUserCount: number
): string {
    if (requiredUserCount === 0) {
        return 'No users yet';
    }
    return `${acceptedUserCount} of ${requiredUserCount} ${requiredUserCount === 1 ? 'user' : 'users'} accepted`;
}

export function formatSignatureExpiry(expiresTs: string | null, now: Date): string {
    if (expiresTs === null) {
        return 'No expiry';
    }
    return new Date(expiresTs).getTime() <= now.getTime()
        ? `Expired ${formatTimestamp(expiresTs)}`
        : `Expires ${formatTimestamp(expiresTs)}`;
}

/** When an assignment is in force, as shown on the Organization's Legal card. */
export function formatAssignmentInForcePeriod(assignment: LegalDocumentAssignmentPublic): string {
    if (assignment.in_force_end === null) {
        return `In force from ${formatTimestamp(assignment.in_force_start)}`;
    }
    return `In force ${formatTimestamp(assignment.in_force_start)} to ${formatTimestamp(assignment.in_force_end)}`;
}

/** Why a past assignment no longer applies. */
export function formatPastAssignmentEnd(assignment: LegalDocumentAssignmentPublic): string {
    if (assignment.revocation !== null) {
        const reason = assignment.revocation.revoke_reason?.trim() ?? '';
        return `Revoked ${formatTimestamp(assignment.revocation.revoked_ts)}${reason.length > 0 ? `: ${reason}` : ''}`;
    }
    if (assignment.in_force_end !== null) {
        return `Ended ${formatTimestamp(assignment.in_force_end)}`;
    }
    return 'No longer active';
}

export function formatAwaitingSignatureBadge(awaitingSignatureCount: number): string | null {
    if (awaitingSignatureCount === 0) {
        return null;
    }
    return `${awaitingSignatureCount} awaiting signature`;
}
